import { ContentCheck, ContentDelete, ContentUndo, NoticeDoNot } from '@fs/zion-icon/dist/cjs/icons'
import { Link, useRouteMatch } from '@fs/zion-router'
import { Button, Cell, DangerButton, Grid, Row, useOverlay } from '@fs/zion-ui'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../app/PageTitleContext'
import useArtifact from '../../hooks/artifacts/useArtifact'
import useArtifactEvents from '../../hooks/artifacts/useArtifactEvents'
import usePatron from '../../hooks/artifacts/usePatron'
import { capitalizeFirstLetter } from '../../util/StringUtil'
import { PermissionsContext } from '../app/PermissionsContext'
import SearchBar from './SearchBar'
import ApproveArtifactOverlay from './overlay/ApproveArtifactOverlay'
import DeleteArtifactOverlay from './overlay/DeleteArtifactOverlay'
import RestoreArtifactOverlay from './overlay/RestoreArtifactOverlay'
import RestrictArtifactOverlay from './overlay/RestrictArtifactOverlay'
import ArtifactInfoPanel from './panel/ArtifactInfoPanel'
import CommentsPanel from './panel/CommentsPanel'
import EventsPanel from './panel/EventsPanel'
import PatronPanel from './panel/PatronPanel'
import RawArtifactDataPanel from './panel/RawArtifactDataPanel'
import TopicTagsPanel from './panel/TopicTagsPanel'
import { SCREENING_STATE, SCREENING_TAG } from './util/artifactsConstants'
import { getMemoriesUrl } from './util/artifactsUtils'
import ArtifactCard from './ArtifactCard'

/**
 * Page component that wraps <ArtifactInfo>. This page is linked to from the
 * Memories Artifact Viewer, SalesForce cases, and elsewhere within DMC.
 */
export default function ArtifactInfo({
  match: {
    params: { artifactId, patronId },
  },
}) {
  usePageView({ page_detail: 'DMConsole:ArtifactInfo' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.artifacts.info'))
  const { isAuthorized, isRole } = useContext(PermissionsContext)
  const { artifact, refetchArtifact } = useArtifact(artifactId, patronId)
  const { events, refetchArtifactsEvents } = useArtifactEvents(artifactId, true)
  const { patron } = usePatron(artifact ? artifact.uploaderId : null, true)
  const route = useRouteMatch()
  const deleteArtifactOverlay = useOverlay()
  const approveArtifactOverlay = useOverlay()
  const restrictArtifactOverlay = useOverlay()
  const restoreArtifactOverlay = useOverlay()
  const showDeleteButton =
    isAuthorized('ArtPrmCanDeleteOtherUsersData') && artifact?.screeningState !== SCREENING_STATE.DELETED
  let showApproveButton =
    isAuthorized('ArtPrmCanRestrictAndApprove') &&
    artifact?.screeningState !== SCREENING_STATE.APPROVED &&
    artifact?.screeningState !== SCREENING_STATE.DELETED
  if (artifact?.screeningState === SCREENING_STATE.RESTRICTED && artifact?.screeningTag === SCREENING_TAG.PORN) {
    showApproveButton = isAuthorized('ArtPrmCanViewPorn')
  }
  const showRestrictButton =
    isAuthorized('ArtPrmCanRestrictAndApprove') &&
    artifact?.screeningState !== SCREENING_STATE.RESTRICTED &&
    artifact?.screeningState !== SCREENING_STATE.DELETED &&
    artifact?.screeningState !== SCREENING_STATE.UNSCREENED
  const showRestoreButton =
    (isAuthorized('ArtPrmCanDeleteOtherUsersData') || isAuthorized('ArtPrmCanRestrictAndApprove')) &&
    artifact?.screeningState === SCREENING_STATE.DELETED

  const screeningTag =
    artifact && artifact.screeningTag && artifact.screeningTag !== 'NOT_SET' ? artifact.screeningTag : null

  const refetchArtifactData = () => {
    refetchArtifact()
    refetchArtifactsEvents()
  }

  if (route.path.includes('tombstoned') && artifact) {
    artifact.screeningState = SCREENING_STATE.DELETED
  }

  return (
    <>
      <SearchBar defaultQuery={artifactId} defaultPrefix="a." />
      <Grid gutters="md">
        {artifact && (
          <Row>
            <Row growX gutters="xs">
              <span>
                {t('artifacts.artifact', 'Artifact')}:{' '}
                <Link to={getMemoriesUrl(artifact.id)} target="_blank" noScrollTop>
                  {artifactId}
                </Link>
              </span>
              {screeningTag && (
                <span>{`${t('artifacts.screeningTag', 'Screening Tag')}: ${t(
                  `artifact.screeningTag.${screeningTag}`,
                  capitalizeFirstLetter(screeningTag)
                )}`}</span>
              )}
            </Row>
            {showApproveButton && (
              <>
                <Button Icon={ContentCheck} emphasis="medium" onClick={approveArtifactOverlay.handleClick}>
                  {t('action.approve', 'Approve')}
                </Button>
                <ApproveArtifactOverlay
                  artifactId={artifactId}
                  onSuccess={refetchArtifactData}
                  {...approveArtifactOverlay}
                />
              </>
            )}
            {showRestrictButton && (
              <>
                <Button Icon={NoticeDoNot} emphasis="medium" onClick={restrictArtifactOverlay.handleClick}>
                  {t('action.restrict', 'Restrict')}
                </Button>
                <RestrictArtifactOverlay
                  artifactId={artifactId}
                  onSuccess={refetchArtifactData}
                  {...restrictArtifactOverlay}
                />
              </>
            )}
            {showRestoreButton && (
              <>
                <Button Icon={ContentUndo} emphasis="medium" onClick={restoreArtifactOverlay.handleClick}>
                  {t('action.restore', 'Restore')}
                </Button>
                <RestoreArtifactOverlay
                  artifactId={artifactId}
                  patronId={artifact.contributorPatronId}
                  onSuccess={refetchArtifactData}
                  {...restoreArtifactOverlay}
                />
              </>
            )}
            {showDeleteButton && (
              <>
                <DangerButton Icon={ContentDelete} onClick={deleteArtifactOverlay.handleClick}>
                  {t('action.delete', 'Delete')}
                </DangerButton>
                <DeleteArtifactOverlay
                  artifactId={artifactId}
                  patronId={patronId}
                  screeningState={artifact.screeningState}
                  onSuccess={refetchArtifactData}
                  {...deleteArtifactOverlay}
                />
              </>
            )}
          </Row>
        )}

        {artifact && (
          <Cell columns={3}>
            <ArtifactCard artifact={artifact} />
          </Cell>
        )}
        <Cell columns={9}>
          {events && <EventsPanel events={events} />}
          {patron && <PatronPanel patron={patron} />}
          {artifact && <ArtifactInfoPanel artifact={artifact} />}
          {artifact && <TopicTagsPanel artifactId={artifactId} />}
          {isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && artifact && (
            <CommentsPanel artifactId={artifactId} />
          )}
          {artifact && <RawArtifactDataPanel artifact={artifact} />}
        </Cell>
      </Grid>
    </>
  )
}
